import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, Mail, FileText } from 'lucide-react';
import SEO from '../components/SEO';

// V5 Design Constants
const v5 = {
  colors: {
    navy: '#1e1b4b',
    cream: '#FEF7ED',
    white: '#FFFFFF',
    purple: '#8b5cf6',
    purpleDark: '#7c3aed',
    orange: '#f97316',
    teal: '#14b8a6',
    amber: '#f59e0b',
    textPrimary: '#1e1b4b',
    textSecondary: 'rgba(30, 27, 75, 0.7)',
    textMuted: 'rgba(30, 27, 75, 0.5)',
    textHint: 'rgba(30, 27, 75, 0.4)',
    borderLight: 'rgba(30, 27, 75, 0.05)',
  },
  fonts: {
    display: "'Faustina', Georgia, serif",
    body: "'DM Sans', sans-serif",
    mono: "'JetBrains Mono', monospace"
  }
};

const Privacy = () => {
  const sections = [
    {
      icon: Eye,
      color: v5.colors.orange,
      title: 'Information We Collect',
      content: 'When you claim an offer through Tappd, we collect your name and email address. This information is used solely to provide you with promotional offers from participating businesses and to measure campaign effectiveness.'
    },
    {
      icon: FileText,
      color: v5.colors.purple,
      title: 'How We Use Your Information',
      content: 'Your information is used to:',
      list: [
        'Deliver promotional offers and promo codes',
        'Track campaign performance and redemption rates',
        'Send occasional updates about new offers (you can opt out anytime)'
      ]
    },
    {
      icon: Lock,
      color: v5.colors.teal,
      title: 'Data Sharing',
      content: 'We do not sell your personal information. We may share your email with participating businesses whose offers you claim, solely for redemption verification purposes.'
    },
    {
      icon: Shield,
      color: v5.colors.amber,
      title: 'Your Rights',
      content: 'You have the right to access, correct, or delete your personal information at any time. Contact us at hello@tappd.app to exercise these rights.'
    },
    {
      icon: Mail,
      color: v5.colors.purple,
      title: 'Contact Us',
      content: 'If you have questions about this Privacy Policy, please contact us at hello@tappd.app'
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: v5.colors.cream }}>
      <SEO 
        title="Privacy Policy"
        description="Tappd's privacy policy explains how we collect, use, and protect your personal information when you use our QR coaster advertising platform."
        url="/privacy"
        noIndex={false}
      />
      {/* V5 Navy Hero Section */}
      <section 
        className="relative overflow-hidden py-20" 
        style={{ backgroundColor: v5.colors.navy, paddingTop: '8rem', boxShadow: '0 4px 20px rgba(30, 27, 75, 0.3)' }}
      >
        <div className="max-w-[920px] mx-auto px-6 text-center relative z-10">
          {/* Code Accent */}
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{ fontFamily: v5.fonts.mono, fontSize: '12px', color: 'rgba(255, 255, 255, 0.4)', marginBottom: '16px' }}
          >
            // privacy_policy
          </motion.p>
          
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 0.8, type: "spring", stiffness: 100 }}
            className="inline-block mb-6"
          >
            <div 
              className="w-20 h-20 rounded-xl flex items-center justify-center mx-auto" 
              style={{ backgroundColor: 'rgba(139, 92, 246, 0.2)' }}
            >
              <Shield size={40} style={{ color: v5.colors.white }} />
            </div>
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-4" 
            style={{
              fontFamily: v5.fonts.display,
              color: v5.colors.white,
              fontSize: 'clamp(2.5rem, 7vw, 4rem)',
              fontWeight: 500
            }}
          >
            Privacy Policy
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            style={{ 
              fontFamily: v5.fonts.body, 
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '16px'
            }}
          >
            Last updated: {new Date().toLocaleDateString()}
          </motion.p>
        </div>

        {/* V5 Gradient Line */}
        <div className="absolute bottom-0 w-full h-1" style={{
          background: `linear-gradient(to right, ${v5.colors.purple} 0%, ${v5.colors.orange} 100%)`
        }} />
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="max-w-[920px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="space-y-6"
          >
            {sections.map((section, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
              >
                <div 
                  className="rounded-xl p-8"
                  style={{
                    backgroundColor: v5.colors.white,
                    border: `1px solid ${v5.colors.borderLight}`,
                    boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)',
                    borderLeft: `4px solid ${section.color}`
                  }}
                >
                  <div className="flex items-start gap-6">
                    <motion.div
                      whileHover={{ rotate: 360, scale: 1.1 }}
                      transition={{ duration: 0.6 }}
                      className="flex-shrink-0"
                    >
                      <div 
                        className="w-14 h-14 rounded-xl flex items-center justify-center" 
                        style={{ backgroundColor: `${section.color}15` }}
                      >
                        <section.icon size={28} style={{ color: section.color }} />
                      </div>
                    </motion.div>
                    <div className="flex-1">
                      <h2 style={{
                        fontFamily: v5.fonts.display,
                        fontSize: '24px',
                        fontWeight: 500,
                        color: v5.colors.textPrimary,
                        marginBottom: '12px'
                      }}>
                        {section.title}
                      </h2>
                      <p style={{
                        fontFamily: v5.fonts.body,
                        fontSize: '16px',
                        color: v5.colors.textSecondary,
                        lineHeight: '1.7',
                        marginBottom: section.list ? '16px' : 0
                      }}>
                        {section.content}
                      </p>
                      {section.list && (
                        <ul className="space-y-3">
                          {section.list.map((item, i) => (
                            <li key={i} className="flex items-start gap-3">
                              <span style={{ color: section.color, fontSize: '20px', lineHeight: '1.5' }}>•</span>
                              <span style={{
                                fontFamily: v5.fonts.body,
                                fontSize: '16px',
                                color: v5.colors.textSecondary
                              }}>
                                {item}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* V5 Bottom Gradient */}
      <div className="h-1" style={{
        background: `linear-gradient(to right, ${v5.colors.purple} 0%, ${v5.colors.orange} 100%)`
      }} />
    </div>
  );
};

export default Privacy;
